import type { CurrencyCode } from "./types";

export const SUPPORTED_CURRENCIES: CurrencyCode[] = [
  "BMD",
  "USD",
  "CAD",
  "EUR",
  "GBP"
];

export const DEFAULT_CURRENCY: CurrencyCode = "USD";

export const FALLBACK_USD_TO_CURRENCY_RATE: Record<CurrencyCode, number> = {
  USD: 1,
  BMD: 1,
  CAD: 1.35,
  EUR: 0.92,
  GBP: 0.79
};

let usdToCurrencyRate: Record<CurrencyCode, number> = {
  ...FALLBACK_USD_TO_CURRENCY_RATE
};

const EURO_COUNTRIES = new Set([
  "AT",
  "BE",
  "CY",
  "DE",
  "EE",
  "ES",
  "FI",
  "FR",
  "GR",
  "HR",
  "IE",
  "IT",
  "LT",
  "LU",
  "LV",
  "MT",
  "NL",
  "PT",
  "SI",
  "SK"
]);

const numberFormatter = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 2
});

const decimalFormatter = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1
});

export function convertUsdToCurrency(value: number, currency: CurrencyCode): number {
  return value * usdToCurrencyRate[currency];
}

export function convertCurrencyToUsd(value: number, currency: CurrencyCode): number {
  return value / usdToCurrencyRate[currency];
}

export function formatCurrency(value: number, currency: CurrencyCode): string {
  const converted = convertUsdToCurrency(value, currency);

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(converted);
}

export function formatWholeCurrency(value: number, currency: CurrencyCode): string {
  const converted = convertUsdToCurrency(value, currency);

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(converted);
}

export function formatNumber(value: number): string {
  return numberFormatter.format(value);
}

export function formatDays(value: number): string {
  return decimalFormatter.format(value);
}

export function getCurrencySymbol(currency: CurrencyCode): string {
  const parts = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    currencyDisplay: "narrowSymbol"
  }).formatToParts(0);

  return parts.find((part) => part.type === "currency")?.value ?? "$";
}

export function currencyFromCountry(countryCode: string | null | undefined): CurrencyCode | null {
  const normalized = (countryCode ?? "").trim().toUpperCase();

  if (!normalized) {
    return null;
  }

  if (normalized === "BM") {
    return "BMD";
  }

  if (normalized === "CA") {
    return "CAD";
  }

  if (normalized === "GB") {
    return "GBP";
  }

  if (EURO_COUNTRIES.has(normalized)) {
    return "EUR";
  }

  if (normalized === "US") {
    return "USD";
  }

  return null;
}

function regionFromLocale(locale: string): string {
  const trimmed = locale.trim();

  if (!trimmed) {
    return "";
  }

  const parts = trimmed.replace("_", "-").split("-");

  return parts.length > 1 ? parts[parts.length - 1].toUpperCase() : "";
}

export function defaultCurrencyFromLocale(locale: string | null | undefined): CurrencyCode {
  const region = regionFromLocale(locale ?? "");
  const regionalCurrency = currencyFromCountry(region);

  return regionalCurrency ?? DEFAULT_CURRENCY;
}

export function getUsdToCurrencyRates(): Record<CurrencyCode, number> {
  return { ...usdToCurrencyRate };
}

export function setUsdToCurrencyRates(
  rates: Partial<Record<CurrencyCode, number>>
): boolean {
  let changed = false;

  for (const currency of SUPPORTED_CURRENCIES) {
    if (!(currency in rates)) {
      continue;
    }

    const rawRate = rates[currency];

    if (typeof rawRate !== "number" || !Number.isFinite(rawRate) || rawRate <= 0) {
      continue;
    }

    const normalizedRate = Number(rawRate.toFixed(6));

    if (usdToCurrencyRate[currency] === normalizedRate) {
      continue;
    }

    usdToCurrencyRate[currency] = normalizedRate;
    changed = true;
  }

  if (usdToCurrencyRate.USD !== 1) {
    usdToCurrencyRate.USD = 1;
    changed = true;
  }

  if (usdToCurrencyRate.BMD !== 1) {
    usdToCurrencyRate.BMD = 1;
    changed = true;
  }

  return changed;
}

export function resetUsdToCurrencyRatesForTests(): void {
  usdToCurrencyRate = { ...FALLBACK_USD_TO_CURRENCY_RATE };
}
